import React from 'react';
import { differenceInMinutes, format } from 'date-fns';
import type {
  AdminWorkerRuntimeConfig,
  AdminWorkerRuntimeConfigEntry,
} from '@brand24-original/llm-types';
import Sparkles from '../../../../shared/components/sparkles/Sparkles';
import { MODEL_LABELS } from '../modelLabels';
import { MonitorType } from '../../../../shared/types/monitor/MonitorType';
import { getMonitorTypeIcon } from '../../../prompt/preview/components/stats/helpers/StatsHelper';
import './WorkerConfigCard.scss';

const GROUP_ICONS: Record<string, string> = {
  Providers: 'ico--zap',
  'Model routing': 'ico--split',
  Features: 'ico--settings',
  Credentials: 'ico--shield-check',
  Concurrency: 'ico--grid-3x3',
  Polling: 'ico--refresh',
};

// The worker publishes every 5 min; past two missed rounds it is probably stuck (it vanishes at 15).
const STALE_AFTER_MINUTES = 11;

const PILLS: Record<string, string> = {
  on: 'worker-config__pill--on',
  set: 'worker-config__pill--on',
  off: 'worker-config__pill--off',
  missing: 'worker-config__pill--missing',
  SEMRUSH: 'worker-config__pill--provider',
  BRIGHTDATA: 'worker-config__pill--provider',
  brightdata: 'worker-config__pill--provider',
  oxylabs: 'worker-config__pill--provider',
};

const Value: React.FC<{ value: string }> = ({ value }) =>
  PILLS[value] ? (
    <span className={`worker-config__pill ${PILLS[value]}`}>{value}</span>
  ) : (
    <span className="worker-config__value">{value}</span>
  );

// Model routing rows are keyed by monitor type: show the model's logo and dashboard name instead.
const Key: React.FC<{ entry: AdminWorkerRuntimeConfigEntry }> = ({ entry }) => {
  const type = entry.key as MonitorType;
  const label = entry.group === 'Model routing' ? MODEL_LABELS[type] : undefined;
  if (!label) {
    return <span className="worker-config__key">{entry.key}</span>;
  }
  const icon = getMonitorTypeIcon(type);
  return (
    <span className="worker-config__model" title={entry.key}>
      {icon && <span className={`ico ico--sm ${icon}`} />}
      {label}
    </span>
  );
};

/** "reported 2 min ago · up since Oct 11, 14:30" — shown in the section title; orange once the worker is stale. */
export const WorkerReportMeta: React.FC<{ worker: AdminWorkerRuntimeConfig }> = ({ worker }) => {
  const minutesAgo = differenceInMinutes(new Date(), new Date(worker.reportedAt));
  return (
    <span
      className={`worker-config__meta${
        minutesAgo >= STALE_AFTER_MINUTES ? ' worker-config__meta--stale' : ''
      }`}
    >
      reported {minutesAgo < 1 ? 'just now' : `${minutesAgo} min ago`} · up since{' '}
      {format(new Date(worker.startedAt), 'MMM d, HH:mm')}
    </span>
  );
};

interface WorkerConfigCardProps {
  worker: AdminWorkerRuntimeConfig;
  /** Several instances reporting: name each card (host + its report time) so they can be told apart. */
  showInstance?: boolean;
}

const WorkerConfigCard: React.FC<WorkerConfigCardProps> = ({ worker, showInstance = false }) => {
  const groups = Array.from(new Set(worker.entries.map((entry) => entry.group)));

  return (
    <div className="worker-config">
      {showInstance && (
        <div className="worker-config__head">
          <span className="worker-config__instance">{worker.instance}</span>
          <WorkerReportMeta worker={worker} />
        </div>
      )}
      <div className="worker-config__groups">
        {groups.map((group) => (
          <div key={group} className="worker-config__group">
            <Sparkles icon={GROUP_ICONS[group] ?? 'ico--settings'} />
            <div className="worker-config__group-title">{group}</div>
            {worker.entries
              .filter((entry) => entry.group === group)
              .map((entry) => (
                <div key={entry.key} className="worker-config__row">
                  <Key entry={entry} />
                  <Value value={entry.value} />
                </div>
              ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default WorkerConfigCard;
